import { useEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'use-intl';
import { MessageSquare } from 'lucide-react';
import { requestOpenFeedback } from '@/lib/showAndTell/openFeedbackEvent';
import { useShieldFromOutsideDismissal } from './useShieldFromOutsideDismissal';
import { useOptionalShowAndTell } from './useShowAndTell';

const OPEN_MODAL_SELECTOR = '[role="dialog"][data-state="open"]:not([data-feedback-panel])';
const hasOpenModal = () => document.querySelector(OPEN_MODAL_SELECTOR) !== null;

function subscribeToModals(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-state'] });
  return () => observer.disconnect();
}

/**
 * An open Radix modal covers the sidebar's Feedback button (AC1, EC13), so while one is open this
 * floats a Feedback button above it; Alt+Shift+F opens the panel from anywhere. Hidden while a
 * recording is running, because the recorder control is already on screen then.
 */
export function ModalFeedbackLauncher() {
  const t = useTranslations('consultantShowAndTell.launcher');
  const api = useOptionalShowAndTell();
  const modalOpen = useSyncExternalStore(subscribeToModals, hasOpenModal, () => false);
  const ref = useRef<HTMLDivElement>(null);
  const available = !!api && api.support.available;

  useEffect(() => {
    if (!available) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey && event.shiftKey && event.code === 'KeyF') {
        event.preventDefault();
        requestOpenFeedback();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [available]);

  useShieldFromOutsideDismissal(ref);

  if (!available || !modalOpen || api.status !== 'idle') return null;

  return createPortal(
    <div ref={ref} data-sat-ignore className="pointer-events-auto fixed bottom-4 right-4 z-[100]">
      <button
        type="button"
        onClick={requestOpenFeedback}
        className="inline-flex h-9 items-center gap-2 rounded-full border bg-background px-4 text-sm font-medium shadow-lg hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
      >
        <MessageSquare className="h-4 w-4" aria-hidden="true" />
        {t('feedback')}
      </button>
    </div>,
    document.body,
  );
}
